/**
 * AdminMcpAccessPanel — control de acceso a la capa MCP.
 *
 * Tres capas: kill switch de plataforma (env), flag global (base de datos, se
 * prende acá) y permiso por usuario. Un usuario necesita las tres en verde
 * más un PAT válido para poder usar MCP.
 */
import { useCallback, useEffect, useState } from 'react';
import {
  Plug, RefreshCw, Loader2, Search, ShieldOff, ShieldCheck,
  AlertTriangle, Power,
} from 'lucide-react';
import { toast } from 'sonner';
import { adminService } from '../../services/adminService';
import McpUserRow from './McpUserRow';
import McpLimitRequests from './McpLimitRequests';

const errMsg = (e, fallback) => (
  e.response?.data?.detail?.message || e.response?.data?.detail || fallback
);

const AccessWarnings = ({ state }) => (
  <>
    {state.access_table_missing && (
      <div className="admin-mcp-warn" data-testid="mcp-access-table-missing">
        <AlertTriangle size={14} />
        Falta la tabla <code>mcp_user_access</code>: corré la migración
        <code>{state.migration}</code> en Supabase.
      </div>
    )}
    {state.kill_switch && (
      <div className="admin-mcp-warn" data-testid="mcp-kill-switch-on">
        <ShieldOff size={14} />
        <strong>MCP_KILL_SWITCH está activo</strong>: la capa MCP responde 404 para todos,
        sin importar los switches de abajo. Se apaga sólo desde <code>backend/.env</code>.
      </div>
    )}
  </>
);

const GlobalSwitch = ({ state, busy, onToggle }) => (
  <div className="admin-mcp-global" data-testid="mcp-global-row">
    <div className="admin-mcp-global-main">
      <Power size={16} className={state.platform_enabled ? 'on' : 'off'} />
      <div>
        <strong>MCP en la plataforma</strong>
        <span>
          {state.platform_enabled
            ? `Activo · ${state.enabled_user_count} usuario(s) habilitado(s)`
            : 'Apagado · ningún cliente MCP puede conectarse'}
        </span>
      </div>
    </div>
    <button
      className={`admin-btn ${state.global_enabled ? 'admin-btn-deduct' : 'admin-btn-grant'}`}
      onClick={onToggle}
      disabled={busy}
      data-testid="mcp-global-toggle"
    >
      {busy
        ? <Loader2 size={12} className="spin" />
        : state.global_enabled ? <ShieldOff size={12} /> : <ShieldCheck size={12} />}
      {state.global_enabled ? 'Apagar MCP' : 'Prender MCP'}
    </button>
  </div>
);

export default function AdminMcpAccessPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search.trim()), 350);
    return () => clearTimeout(t);
  }, [search]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await adminService.getMcpAccess({ search: debounced }));
    } catch (e) {
      toast.error(errMsg(e, 'No se pudo cargar el acceso MCP'));
    } finally {
      setLoading(false);
    }
  }, [debounced]);

  useEffect(() => { load(); }, [load]);

  const toggleGlobal = useCallback(async () => {
    const next = !data.global_enabled;
    if (next && !window.confirm(
      'Vas a prender MCP para toda la plataforma. Igual sólo podrán usarlo los '
      + 'usuarios habilitados uno por uno. ¿Continuar?'
    )) return;
    setBusy('global');
    try {
      const res = await adminService.setMcpGlobal(next);
      setData((d) => ({ ...d, ...res }));
      toast.success(next ? 'MCP habilitado en la plataforma' : 'MCP apagado en la plataforma');
    } catch (e) {
      toast.error(errMsg(e, 'No se pudo cambiar el flag global'));
    } finally { setBusy(''); }
  }, [data]);

  const toggleUser = useCallback(async (u) => {
    setBusy(u.user_id);
    try {
      const res = await adminService.setMcpUser(u.user_id, !u.mcp_enabled);
      setData((d) => ({
        ...d,
        enabled_user_count: d.enabled_user_count + (res.mcp_enabled ? 1 : -1),
        users: d.users.map((x) => (
          x.user_id === u.user_id ? { ...x, mcp_enabled: res.mcp_enabled } : x
        )),
      }));
      toast.success(res.mcp_enabled
        ? `MCP habilitado para ${u.email || u.user_id}`
        : `MCP deshabilitado para ${u.email || u.user_id} (sus tokens siguen existiendo)`);
    } catch (e) {
      toast.error(errMsg(e, 'No se pudo cambiar el permiso'));
    } finally { setBusy(''); }
  }, []);

  const saveLimits = useCallback(async (u, limits) => {
    setBusy(u.user_id);
    try {
      const res = await adminService.setMcpUserLimits(u.user_id, limits);
      setData((d) => ({
        ...d,
        users: d.users.map((x) => (
          x.user_id === u.user_id ? { ...x, limits: res.limits } : x
        )),
      }));
      toast.success('Presupuesto MCP actualizado');
    } catch (e) {
      toast.error(errMsg(e, 'No se pudieron guardar los límites'));
    } finally { setBusy(''); }
  }, []);

  if (loading && !data) {
    return (
      <div className="admin-health-loading" data-testid="mcp-access-loading">
        <Loader2 size={18} className="animate-spin" /> Cargando acceso MCP…
      </div>
    );
  }
  if (!data) return null;

  return (
    <div className="admin-mcp-panel" data-testid="mcp-access-panel">
      <div className="admin-health-head">
        <div className="admin-health-title">
          <Plug size={15} />
          <span>Acceso MCP</span>
          <em>
            requisitos: kill switch apagado + flag global ON + usuario habilitado + PAT válido
          </em>
        </div>
        <button className="admin-refresh" onClick={load} data-testid="mcp-access-refresh">
          <RefreshCw size={13} /> Actualizar
        </button>
      </div>

      <AccessWarnings state={data} />
      <GlobalSwitch state={data} busy={busy === 'global'} onToggle={toggleGlobal} />

      <McpLimitRequests
        onApproved={(userId, limits) => setData((d) => ({
          ...d,
          users: d.users.map((x) => (
            x.user_id === userId && limits ? { ...x, limits } : x
          )),
        }))}
      />

      <div className="admin-mcp-search">
        <Search size={14} />
        <input
          type="text"
          placeholder="Buscar usuario por email, nombre o ID…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          data-testid="mcp-access-search"
        />
        {loading && <Loader2 size={13} className="spin" />}
      </div>

      <div className="admin-mcp-users" data-testid="mcp-access-users">
        {(data.users || []).length === 0 && <div className="admin-empty">Sin usuarios</div>}
        {(data.users || []).map((u) => (
          <McpUserRow
            key={u.user_id}
            user={u}
            busy={busy === u.user_id}
            onToggle={toggleUser}
            onSaveLimits={saveLimits}
          />
        ))}
      </div>

      <p className="admin-tab-help">
        <AlertTriangle size={11} /> Quitar el permiso no revoca los tokens: dejan de
        funcionar mientras el usuario esté deshabilitado y vuelven a andar si lo habilitás
        de nuevo. La autorización va atada al <code>user_id</code> del PAT, no a la sesión
        del navegador.
      </p>
    </div>
  );
}
